/**
 * Groups the agent transcript into segments the shared Chat primitives can render (FEAT-1698).
 *
 * `MessageList` renders a contiguous run of user/assistant bubbles, so consecutive message items are
 * batched into one run and tool-activity / error rows split the runs apart. That keeps the message
 * area on the shared primitive while still interleaving agent tool activity in stream order.
 */
import { ChatRole } from '@modernloop/shared/components';
import type { ChatMessage } from '@modernloop/shared/components';

import { AGENT_CHAT_ITEM } from './chatItems';
import type { AgentChatItem, AgentToolStatus } from './chatItems';

/** Discriminants for transcript segments handed to the shared Chat primitives. */
export const AGENT_TRANSCRIPT_SEGMENT = {
  MESSAGE_RUN: 'MESSAGE_RUN',
  TOOL_ACTIVITY: 'TOOL_ACTIVITY',
  TURN_ERROR: 'TURN_ERROR',
} as const;

/** A renderable chat bubble carries its source item id so React never relies on list positions. */
export type AgentTranscriptMessage = ChatMessage & { key: string; isStreaming?: boolean };

/** One renderable slice of the transcript: a run of chat bubbles, a tool row, or an error row. */
export type AgentTranscriptSegment =
  | {
      type: typeof AGENT_TRANSCRIPT_SEGMENT.MESSAGE_RUN;
      key: string;
      messages: AgentTranscriptMessage[];
    }
  | {
      type: typeof AGENT_TRANSCRIPT_SEGMENT.TOOL_ACTIVITY;
      key: string;
      toolName: string;
      status: AgentToolStatus;
    }
  | {
      type: typeof AGENT_TRANSCRIPT_SEGMENT.TURN_ERROR;
      key: string;
      code: string;
      localizedMessage: string | null;
    };

/** Stable key for the synthetic trailing run that hosts the "thinking" bubble. */
export const AGENT_PENDING_SEGMENT_KEY = 'PENDING';

/**
 * Splits transcript items into `MessageList`-friendly runs separated by tool and error rows.
 *
 * `withPendingBubble` appends an empty trailing run so `MessageList` can own the loading bubble
 * instead of the panel rendering a second, differently-styled one.
 */
export function toTranscriptSegments(
  items: readonly AgentChatItem[],
  withPendingBubble = false
): AgentTranscriptSegment[] {
  const segments: AgentTranscriptSegment[] = [];

  const appendMessage = (key: string, message: Omit<AgentTranscriptMessage, 'key'>) => {
    const last = segments[segments.length - 1];
    if (last?.type === AGENT_TRANSCRIPT_SEGMENT.MESSAGE_RUN) {
      last.messages.push({ ...message, key });
      return;
    }
    segments.push({
      type: AGENT_TRANSCRIPT_SEGMENT.MESSAGE_RUN,
      key,
      messages: [{ ...message, key }],
    });
  };

  items.forEach((item) => {
    switch (item.type) {
      case AGENT_CHAT_ITEM.USER_MESSAGE:
        appendMessage(item.id, { role: ChatRole.USER, content: item.text });
        break;
      case AGENT_CHAT_ITEM.ASSISTANT_MESSAGE:
        const message: Omit<AgentTranscriptMessage, 'key'> = {
          role: ChatRole.AGENT,
          content: item.text,
        };
        if (item.isStreaming) {
          message.isStreaming = true;
        }
        appendMessage(item.id, message);
        break;
      case AGENT_CHAT_ITEM.TOOL_ACTIVITY:
        segments.push({
          type: AGENT_TRANSCRIPT_SEGMENT.TOOL_ACTIVITY,
          key: item.id,
          toolName: item.toolName,
          status: item.status,
        });
        break;
      case AGENT_CHAT_ITEM.TURN_ERROR:
        segments.push({
          type: AGENT_TRANSCRIPT_SEGMENT.TURN_ERROR,
          key: item.id,
          code: item.code,
          localizedMessage: item.localizedMessage,
        });
        break;
      default:
        break;
    }
  });

  if (withPendingBubble) {
    segments.push({
      type: AGENT_TRANSCRIPT_SEGMENT.MESSAGE_RUN,
      key: AGENT_PENDING_SEGMENT_KEY,
      messages: [],
    });
  }

  return segments;
}
